import React, { useState } from 'react';
import { toast } from "react-toastify";
import Popup from "reactjs-popup";
import {
	ButtonGroup,
	DropdownButton,
	Dropdown,
	Col,
	Row,
} from "react-bootstrap";
import type { fakeRootType } from "../FakeRootType";
import QuoteWarningsHistory from "./NewItems/QuoteWarningsHistory";
import apiCalls from "../PowerShadesAPIFunctions";
import { useQuoteById, useQuoteTypeNameByQuoteId } from "../Store/entities/quotes/hooks";
import { useAssemblyOptions } from "../Store/entities/assemblyOptions/hooks";
import { useAssembliesByQuoteId } from "../Store/entities/assemblies/hooks";
import { updateAssemblies } from "../Store/entities/assemblies";

import { useIsEngineering, useIsMfgAdmin } from "../hooks";
import { UsePortalDispatch, UsePortalSelector } from "../Store";
import { AssemblyStore } from "../Store/entities/assemblies/types";
import { AssemblyOptionsViewModel } from "../powershadesApiTypes";

type QuoteAdminBarProps = {
	showCostBreakdown: () => void;
	showHistory: () => void;
	showFabricAllowedIndoor: () => void;
	showFabricAllowedOutdoor: () => void;
	showPopulateDashboardTasks: () => void;
	setIsAddingCOROutdoorShade: (isAdding: boolean) => void;
	quoteId: number;
	showQuoteReviews: () => void;
	ROOT: fakeRootType;
};

const normalizeString = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const getHeaderSeriesForAssembly = (assembly: AssemblyStore) => {
	if (assembly.shade_type_id === 1) return "indoor_roller_shade";
	if (assembly.shade_type_id === 2) return "corradi_roller_shade";
	if (assembly.shade_type_id === 3) return "outdoor_roller_shade";
	return "";
};

const getHeaderCandidates = (assembly: AssemblyStore, assemblyOptions: AssemblyOptionsViewModel) => {
	const series = getHeaderSeriesForAssembly(assembly);
	return assemblyOptions?.headers?.filter((header) => header?.series_attatched_to_header?.includes(series)) ?? [];
};

const findBestHeaderMatch = (assembly: AssemblyStore, headers: AssemblyOptionsViewModel["headers"]) => {
	const currentHeaderLine = normalizeString(assembly.header_line);
	if (!currentHeaderLine) return undefined;

	return headers.find((header) => {
		const name = normalizeString(header.name);
		const sku = normalizeString(header.header_sku);
		return name === currentHeaderLine || sku === currentHeaderLine || name.includes(currentHeaderLine) || currentHeaderLine.includes(name) || sku.includes(currentHeaderLine) || currentHeaderLine.includes(sku);
	});
};

const tryPickSingleHeaderForAssembly = (assembly: AssemblyStore, headers: AssemblyOptionsViewModel["headers"]) => {
	const shade = assembly.shades?.[0];
	const isMotorized = normalizeString(shade?.shade_type) === "motorized" || !!normalizeString(shade?.motor_type);
	const isManual = normalizeString(shade?.shade_type) === "manual" || !!normalizeString(shade?.manual_chain);

	const filtered = headers.filter((header) => {
		if (isMotorized) {
			return header.drive_types?.some((dt) => dt.shade_type === "motorized");
		}
		if (isManual) {
			return header.drive_types?.some((dt) => dt.shade_type === "manual");
		}
		return true;
	});

	return filtered.length === 1 ? filtered[0] : undefined;
};

const repairAssembly = (assembly: AssemblyStore, assemblyOptions: AssemblyOptionsViewModel) => {
	const headerCandidates = getHeaderCandidates(assembly, assemblyOptions);
	if (!headerCandidates.length) return assembly;

	const matchedHeader = findBestHeaderMatch(assembly, headerCandidates) ?? tryPickSingleHeaderForAssembly(assembly, headerCandidates);

	let repaired = { ...assembly };

	const targetHeader = matchedHeader ?? headerCandidates[0];
	if (targetHeader && repaired.header_line && normalizeString(repaired.header_line) !== normalizeString(targetHeader.name)) {
		const validHeaderNames = headerCandidates.map(h => h.name);
		if (!validHeaderNames.some(name => normalizeString(name) === normalizeString(repaired.header_line))) {
			repaired.header_line = targetHeader.name;
		}
	}

	if (targetHeader) {
		const validMountTypes = targetHeader.mount_types?.map(mt => mt.name) ?? [];
		if (repaired.mount_style && !validMountTypes.includes(repaired.mount_style)) {
			if (targetHeader.mount_types && targetHeader.mount_types.length > 0 && targetHeader.mount_types[0]) {
				repaired.mount_style = targetHeader.mount_types[0].name;
			}
		}

		const validChannels = targetHeader.channels?.map(ch => ch.value) ?? [];
		if (repaired.side_channels && !validChannels.includes(repaired.side_channels)) {
			if (targetHeader.channels && targetHeader.channels.length > 0 && targetHeader.channels[0]) {
				repaired.side_channels = targetHeader.channels[0].value;
			}
		}

		const validEndCaps = targetHeader.end_caps?.map(ec => ec.value) ?? [];
		if (repaired.end_caps && !validEndCaps.includes(repaired.end_caps)) {
			if (targetHeader.end_caps && targetHeader.end_caps.length > 0 && targetHeader.end_caps[0]) {
				repaired.end_caps = targetHeader.end_caps[0].value;
			}
		}
	}

	return repaired;
};

// Only uncomment below for debugging.
// const debugBreakHeaderSelection = (assembly: AssemblyStore, assemblyOptions: AssemblyOptionsViewModel) => {
// 	const headerCandidates = getHeaderCandidates(assembly, assemblyOptions);
// 	if (!headerCandidates.length || !assembly.header_line) return assembly;

// 	const matchedHeader = findBestHeaderMatch(assembly, headerCandidates) ?? headerCandidates.find((header) => normalizeString(header.name) === normalizeString(assembly.header_line));
// 	if (!matchedHeader) return assembly;

// 	let broken = { ...assembly };

// 	const shade = broken.shades?.[0];
// 	const driveTypes = matchedHeader.drive_types?.map((dt) => dt.shade_type).filter(Boolean) ?? [];
// 	const onlyManual = driveTypes.length === 1 && driveTypes[0] === "manual";
// 	const onlyMotorized = driveTypes.length === 1 && driveTypes[0] === "motorized";

// 	if (shade && broken.shade_type_id === 1 && (onlyManual || onlyMotorized)) {
// 		broken = {
// 			...broken,
// 			shades: broken.shades.map((s, index) => index === 0 ? {
// 				...s,
// 				shade_type: onlyManual ? "motorized" : "manual",
// 				manual_chain: onlyManual ? s.manual_chain : "spring",
// 				motor_type: onlyManual ? s.motor_type : s.motor_type,
// 			} : s)
// 		};
// 		return broken;
// 	}

// 	if (matchedHeader.header_sku && normalizeString(matchedHeader.header_sku) !== normalizeString(matchedHeader.name)) {
// 		broken.header_line = matchedHeader.header_sku;
// 		return broken;
// 	}

// 	broken.header_line = `${matchedHeader.name} [debug]`;
// 	return broken;
// };

const QuoteAdminBar = ({
	showCostBreakdown,
	showHistory,
	showFabricAllowedIndoor,
	showFabricAllowedOutdoor,
	showPopulateDashboardTasks,
	setIsAddingCOROutdoorShade,
	quoteId,
	showQuoteReviews,
	ROOT
}: QuoteAdminBarProps) => {
	const override24Snapshot = () => {
		apiCalls.override24HourHold(quoteId)
			.then(() => {
				toast.success("24 Hour Hold Overridden");
				window.location.reload();
			}).catch(() => {
				toast.error("Failed to Override 24 Hour Hold");
			});
	};

	const isMfgAdmin = useIsMfgAdmin();
	const isEngineering = useIsEngineering();
	const dispatch = UsePortalDispatch();
	const assemblies = useAssembliesByQuoteId(quoteId);
	const assemblyOptions = UsePortalSelector(useAssemblyOptions(quoteId));

	const [quoteWarningsOpen, setQuoteWarningsOpen] = useState(false);

	const quote = useQuoteById(quoteId);
	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	const isHoldOverriden = quote.is_hold_overridden ?? false;

	const archived = quote.archived ?? false;

	const restoreQuote = async () => {
		try {
			await apiCalls.restoreQuote(quoteId);
			toast.success("Quote Restored!");
		} catch {
			toast.error("Failed to Restore Quote.");
		}
	};

	const repriceQuote = () => {
		apiCalls.repriceWholeQuote(quoteId)
			.then(() => {
				toast.success("Quote Repriced Successfully");
				window.location.reload();
			})
			.catch(() => {
				toast.error("Failed to Reprice Quote");
			});
	};

	const repairAssemblyState = async () => {
		if (!assemblies?.length) {
			toast.info("No assemblies found on this quote.");
			return;
		}

		if (!assemblyOptions?.headers?.length) {
			toast.info("Assembly options are not loaded yet.");
			return;
		}

		const repaired = assemblies.map((assembly) => repairAssembly(assembly, assemblyOptions));
		const changedAssemblies = repaired.filter((assembly, index) => JSON.stringify(assembly) !== JSON.stringify(assemblies[index]));

		if (!changedAssemblies.length) {
			toast.info("No assembly state repairs were required.");
			return;
		}

		try {
			const result = await dispatch(updateAssemblies({ quoteId, assemblies: repaired }));
			if ((result as any)?.meta?.requestStatus === "fulfilled") {
				toast.success(`Repaired ${changedAssemblies.length} assembly${changedAssemblies.length === 1 ? "" : "ies"}.`);
			} else {
				toast.error("Failed to repair assembly state.");
			}
		} catch {
			toast.error("Failed to repair assembly state.");
		}
	};

	// Only uncomment below for debugging.
	// const debugDeselectHeader = async () => {
	// 	if (!assemblies?.length) {
	// 		toast.info("No assemblies found on this quote.");
	// 		return;
	// 	}

	// 	if (!assemblyOptions?.headers?.length) {
	// 		toast.info("Assembly options are not loaded yet.");
	// 		return;
	// 	}

	// 	const broken = assemblies.map((assembly) => debugBreakHeaderSelection(assembly, assemblyOptions));
	// 	const changedAssemblies = broken.filter((assembly, index) => JSON.stringify(assembly) !== JSON.stringify(assemblies[index]));

	// 	if (!changedAssemblies.length) {
	// 		toast.info("No assemblies could be set to the debug deselect state.");
	// 		return;
	// 	}

	// 	try {
	// 		const result = await dispatch(updateAssemblies({ quoteId, assemblies: broken }));
	// 		if ((result as any)?.meta?.requestStatus === "fulfilled") {
	// 			toast.success(`Debugged ${changedAssemblies.length} assembly${changedAssemblies.length === 1 ? "" : "ies"}.`);
	// 		} else {
	// 			toast.error("Failed to apply debug deselect state.");
	// 		}
	// 	} catch {
	// 		toast.error("Failed to apply debug deselect state.");
	// 	}
	// };

	return (
		<Row className="justify-content-end">
			<Col xl={9} lg={12} md={6} sm={6} xs={12} className="mt-2">
				<DropdownButton
					as={ButtonGroup}
					variant={quoteType || 'cedia'}
					title="Admin Actions"
				>
					{!isHoldOverriden && isMfgAdmin && <Dropdown.Item eventKey="1" onClick={override24Snapshot}>
						OVERRIDE 24 HOUR HOLD
					</Dropdown.Item>}
					<Dropdown.Item hidden={!isEngineering} onClick={repriceQuote}>
						Recalculate All Pricing
					</Dropdown.Item>
					<Dropdown.Item eventKey="1" onClick={showCostBreakdown}>
						View Cost Breakdown
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={showHistory}>
						View History
					</Dropdown.Item>
					<Dropdown.Item eventKey="3" onClick={showPopulateDashboardTasks}>
						Dashboard Tasks
					</Dropdown.Item>
					<Dropdown.Item onClick={() => setQuoteWarningsOpen(true)}>Quote Warnings</Dropdown.Item>
					{isEngineering && <Dropdown.Item onClick={repairAssemblyState}>Repair Assembly State</Dropdown.Item>}
					{/* Only uncomment below for debugging. */}
					{/* {isEngineering && <Dropdown.Item onClick={debugDeselectHeader}>Deselect Header (Debug)</Dropdown.Item>} */}
					<Dropdown.Item onClick={() => setIsAddingCOROutdoorShade(true)}>Add Corradi Outdoor</Dropdown.Item>
					<Dropdown.Item hidden={!archived} onClick={restoreQuote}>Restore Quote</Dropdown.Item>
					<Dropdown.Item
						eventKey="4"
						onClick={showFabricAllowedIndoor}
					>
						Enable Disc. Indoor Fabrics
					</Dropdown.Item>
					<Dropdown.Item
						eventKey="5"
						onClick={showFabricAllowedOutdoor}
					>
						Enable Disc. PsOutdoor Fabrics
					</Dropdown.Item>
					<Dropdown.Item
						eventKey="6"
						onClick={() => showQuoteReviews()}
					>
						Show Quote Reviews
					</Dropdown.Item>
					<Dropdown.Item
						onClick={() => {
							ROOT.alert({
								title: "Create Discount Review?",
								text: "This will set the quote status to Awaiting Sales Approval.",
								icon: "warning",
							})
								.then(() => {
									apiCalls.createDiscountReview(quoteId)
										.then(() => {
											toast.success("Discount review created.");
											window.location.reload();
										})
										.catch(() => toast.error("Failed to create discount review."));
								})
								.catch(() => { /* cancelled */ });
						}}
					>
						Create Discount Review
					</Dropdown.Item>
				</DropdownButton>
			</Col>
			<Col xl={9} lg={12} md={6} sm={6} xs={12} className="mt-2">
			</Col>
			<Popup
				modal
				className="qwh mobile-modal"
				open={quoteWarningsOpen}
			>
				<QuoteWarningsHistory
					quoteId={quoteId}
					close={() => setQuoteWarningsOpen(false)}
				/>
			</Popup>
		</Row>
	);
};

export default QuoteAdminBar;
